import { LitElement, property, html } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import '@polleverywhere/pe-button'
import '@polleverywhere/advisory-message'
import './style.scss'

const TIMEOUT = 5 * 60 * 1000
/**
 * `pe-webex-auth` is a minimal component that allows the user to sign up
 * and login, while also providing optional information via a slot
 * (i.e. a link to external support articles).
 *
 * @element pe-webex-auth
 *
 * @prop {string} mode - Flag used to determine the screen to show
 *                       auth: login or sign up screen
 *                       waiting: waiting screen for out of band auth
 *
 * @slot - Unnamed slot can be used for optional information to display
 *
 * @fires click:button - Fires when a button is clicked. The event's detail.name will be the name of the button clicked.
 * @fires reset - Fires when auth flow is reset. Can happen after a timeout or manual change to 'auth' state.
 */
export default class PeWebexAuth extends LitElement {
  static get tagName () { return 'pe-webex-auth' }

  @property({ type: String })
  mode = 'auth'

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  render () {
    return html`
      <div class=${bem('main')}>
        ${renderContent.call(this)}
      </div>

      <div class=${bem('footer')}>
        ${this.slots()}
      </div>
    `
  }
}

function renderContent () {
  clearTimeout(this.timer)
  if (/waiting/.test(this.mode)) {
    this.timer = setTimeout(() => {
      this.mode = 'timeout'
      this.dispatchEvent(new CustomEvent('reset', {
        bubbles: true
      }))
    }, TIMEOUT)
    return renderWaiting.call(this)
  } else if (this.mode === 'timeout') {
    return renderTimeout.call(this)
  } else {
    this.dispatchEvent(new CustomEvent('reset', {
      bubbles: true
    }))
    return renderAuth.call(this)
  }
}

function message () {
  return `${/login/.test(this.mode)
    ? 'Please log in to your Poll Everywhere account in the pop-up window. '
    : 'Please finish signing up for a Poll Everywhere account in the pop-up window. '}
If you don’t see it, please check the pop-up settings in your browser.`
}

function renderWaiting () {
  return html`
    <advisory-message
      icon=restart
      spinIcon=true
      subject='Awaiting ${/login/.test(this.mode) ? 'login' : 'sign up'}'
      message=${message.call(this)}
      @abort=${() => {
        this.mode = 'auth'
      }}
    ></advisory-message>
    <pe-button label='Back to home' @click=${() => { this.mode = 'auth' }}></pe-button>
    `
}

function renderTimeout (type) {
  return html`
    <advisory-message
      icon='alert'
      subject='We could not finish'
      message='Something went wrong during the process. Please close the pop-up window and try again.'
      @abort=${() => {
        this.mode = 'auth'
      }}
    ></advisory-message>
    <pe-button label='Try again' @click=${() => { this.mode = 'auth' }}></pe-button>
    `
}

function renderAuth () {
  return html`
    <span class=${bem('title')}>Welcome!</span>
    <pe-graphic class=${bem('illustration')}
                type='illustration'
                name='hybrid-meetings'
    ></pe-graphic>
    <div class=${bem('buttons')}>
      <pe-button
        @click=${handleClick}
        name='login'
        label='Login or Sign up'
      ></pe-button>
    </div>
  `
}

function handleClick (e) {
  const name = e?.currentTarget?.name
  if (!name) return

  this.mode = `waiting_${name}`
  this.dispatchEvent(new CustomEvent('click:button', {
    bubbles: true,
    detail: { name }
  }))
}

const bem = bemBlock(PeWebexAuth.tagName)
register(module, PeWebexAuth.tagName, PeWebexAuth)
